// lib/dokumente/briefDaten.ts
// #539 (Ausbau): Empfänger-Anschrift, Platzhalter-Werte und Übernahme einer Brief-Vorlage
// (dokument_vorlagen, HTML mit {{gruppe.feld}}) in den Blanko-Brief — reine Funktionen.

const ENTITAETEN: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", euro: '€', auml: 'ä', ouml: 'ö', uuml: 'ü', Auml: 'Ä', Ouml: 'Ö', Uuml: 'Ü', szlig: 'ß' };

/** HTML → Klartext mit Zeilenumbrüchen (Absätze, Tabellenzeilen, Listenpunkte). */
export function htmlZuText(html: string): string {
  return html
    .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<\s*\/\s*(td|th)\s*>/gi, '   ')
    .replace(/<\s*hr[^>]*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|tr|li|ul|ol|table)\s*>/gi, '\n')
    .replace(/<\s*(p|h[1-6])[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (m, e: string) => {
      if (e[0] === '#') { const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10); return Number.isFinite(n) ? String.fromCodePoint(n) : m; }
      return ENTITAETEN[e] ?? m;
    })
    .split('\n').map((z) => z.replace(/[ \t ]+/g, ' ').trim()).join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const PLATZHALTER = /\{\{\s*([a-z_]+(?:\.[a-z_]+)+)\s*\}\}/gi;

/** Platzhalter ersetzen; unbekannte bleiben sichtbar als {{key}}, damit sie auffallen. */
export function fuellePlatzhalter(html: string, werte: Readonly<Record<string, string>>): string {
  return html.replace(PLATZHALTER, (m, roh: string) => {
    const w = werte[roh.toLowerCase()];
    return w !== undefined && w !== '' ? w.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : m;
  });
}

/**
 * Vorlage → Betreff + Text für den Blanko-Brief. Die erste Überschrift wird Betreff.
 * Steht davor der alte Briefkopf (Absender, Empfänger, {{datum.heute…}}), entfällt er —
 * Briefpapier, Anschriftfeld und Datum übernimmt der Blanko-Brief. Sonst bleibt der Vorspann.
 */
export function vorlageZuBrief(html: string, werte: Readonly<Record<string, string>>): { betreff: string; text: string } {
  const m = /<\s*(h[1-3])[^>]*>([\s\S]*?)<\s*\/\s*\1\s*>/i.exec(html);
  if (!m) return { betreff: '', text: htmlZuText(fuellePlatzhalter(html, werte)) };
  const vorspann = html.slice(0, m.index);
  const rest = html.slice(m.index + m[0].length);
  const istBriefkopf = /\{\{\s*datum\.heute/i.test(vorspann);
  const betreff = htmlZuText(fuellePlatzhalter(m[2], werte)).replace(/\n+/g, ' ');
  const text = htmlZuText(fuellePlatzhalter((istBriefkopf ? '' : vorspann) + rest, werte));
  return { betreff, text };
}

export interface Anschrift {
  anrede?: string | null; vorname?: string | null; nachname?: string | null; firma?: string | null; co?: string | null;
  strasse?: string | null; hausnummer?: string | null; plz?: string | null; ort?: string | null; zusatz?: string | null;
}

const t = (s: string | null | undefined) => (s ?? '').trim();

/** Anschriftzeilen fürs Fensterkuvert: „Herrn“/„Frau“, Name bzw. Firma, c/o, Straße, PLZ Ort. */
export function anschriftText(a: Anschrift): string {
  const anredeZeile = anschriftAnrede(a.anrede);
  const name = [t(a.vorname), t(a.nachname)].filter(Boolean).join(' ');
  const zeilen = [
    t(a.firma),
    name ? [anredeZeile, name].filter(Boolean).join(' ') : '',
    t(a.co) ? `c/o ${t(a.co)}` : '',
    t(a.zusatz),
    [t(a.strasse), t(a.hausnummer)].filter(Boolean).join(' '),
    [t(a.plz), t(a.ort)].filter(Boolean).join(' '),
  ].filter(Boolean);
  return zeilen.join('\n');
}

/** Kern der Anrede ohne „Sehr geehrte(r)“ – Altdaten enthalten oft die ganze Briefanrede. */
const anredeKern = (anrede: string | null | undefined) => t(anrede).toLowerCase().replace(/^sehr\s+geehrter?\s*/, '');
const FRAU = /\bfrau\b/, HERR = /\bherrn?\b/; // nicht „Herren“ (Damen und Herren)

/** Erste Anschriftzeile: „Herrn“, „Frau“, „Frau und Herrn“, „Eheleute“, „Familie“ – sonst nichts (Divers, Firma). */
function anschriftAnrede(anrede: string | null | undefined): string {
  const k = anredeKern(anrede);
  if (/\beheleute\b/.test(k)) return 'Eheleute';
  if (/\bfamilie\b/.test(k)) return 'Familie';
  if (FRAU.test(k) && HERR.test(k)) return 'Frau und Herrn';
  if (k.startsWith('herr') && HERR.test(k)) return 'Herrn';
  if (k.startsWith('frau')) return 'Frau';
  return '';
}

/**
 * Briefanrede. Vollständige Altanreden mit Namen („Sehr geehrte Eheleute Bender und Hahn“) bleiben unverändert;
 * kurze (Herr, Frau, Eheleute, Familie, Frau/Herr – auch „Sehr geehrter Herr“ ohne Namen) bekommen den Nachnamen;
 * Divers → „Guten Tag Vorname Nachname,“. Ohne Nachnamen neutral.
 */
export function briefAnrede(a: { anrede?: string | null; vorname?: string | null; nachname?: string | null }): string {
  const roh = t(a.anrede).replace(/[\s,.;:]+$/, '');
  if (/^sehr\s+geehrte/i.test(roh) && !/^sehr\s+geehrter?\s+(frau|herr|eheleute|familie)$/i.test(roh)) return `${roh},`;
  const k = anredeKern(roh);
  const n = t(a.nachname);
  if (/\bdivers\b/.test(k)) { const name = [t(a.vorname), n].filter(Boolean).join(' '); if (name) return `Guten Tag ${name},`; }
  if (n) {
    if (/\beheleute\b/.test(k)) return `Sehr geehrte Eheleute ${n},`;
    if (/\bfamilie\b/.test(k)) return `Sehr geehrte Familie ${n},`;
    if (FRAU.test(k) && HERR.test(k)) return `Sehr geehrte Frau ${n}, sehr geehrter Herr ${n},`;
    if (k.startsWith('frau')) return `Sehr geehrte Frau ${n},`;
    if (k.startsWith('herr') && HERR.test(k)) return `Sehr geehrter Herr ${n},`;
  }
  return 'Sehr geehrte Damen und Herren,';
}

/** Platzhalter der Gruppe „Empfänger“ – gleich für Mieter, Eigentümer und Kontakte. */
export function empfaengerWerte(a: { anrede?: string | null; vorname?: string | null; nachname?: string | null; firma?: string | null }): Record<string, string> {
  const name = [t(a.vorname), t(a.nachname)].filter(Boolean).join(' ') || t(a.firma);
  return { 'empfaenger.briefanrede': briefAnrede(a), 'empfaenger.name': name, 'empfaenger.vorname': t(a.vorname), 'empfaenger.nachname': t(a.nachname) || t(a.firma), 'empfaenger.firma': t(a.firma) };
}

export const euro = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(Number(n)) ? '' : `${Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const datumDe = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t(iso));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

export interface MieterDaten {
  anrede?: string | null; vorname?: string | null; nachname?: string | null; firma_name?: string | null;
  email?: string | null; telefon?: string | null; mobil?: string | null; geburtsdatum?: string | null;
  mietbeginn?: string | null; mietende?: string | null; kaltmiete?: number | null; nebenkosten_vorauszahlung?: number | null;
  heizkosten?: number | null; kaution?: number | null; kundennummer?: string | null;
  // #590 (Immoware-Platzhalter): Bank, SEPA, Zahlweise
  weitere_kosten?: number | null; zahlungsweise?: string | null; bankverbindung?: string | null; mieter_iban?: string | null;
  mieter_kontoinhaber?: string | null; sepa_mandatsreferenz?: string | null; sepa_mandatsdatum?: string | null; anzahl_bewohner?: number | null;
}
export interface ObjektDaten {
  bezeichnung?: string | null; strasse?: string | null; hausnummer?: string | null; plz?: string | null; stadt?: string | null;
  objektnummer?: string | null; objekttyp?: string | null; baujahr?: number | string | null; heizungsart?: string | null;
  wohnflaeche?: number | string | null; verwaltungsart?: string | null; energieklasse?: string | null;
}
export interface EigentuemerDaten {
  anrede?: string | null; vorname?: string | null; nachname?: string | null; firma_name?: string | null;
  strasse?: string | null; hausnummer?: string | null; plz?: string | null; stadt?: string | null;
  zustell_strasse?: string | null; zustell_hausnummer?: string | null; zustell_plz?: string | null; zustell_stadt?: string | null;
  email?: string | null; telefon?: string | null; mobil?: string | null; bankverbindung?: string | null; bic?: string | null; kontoinhaber?: string | null;
  glaeubiger_id?: string | null; sepa_mandatsreferenz?: string | null; kundennummer?: string | null; vertragsstart?: string | null; vertragsende?: string | null;
}
/** #592: Wohnung des Mieters und alle, die dort wohnen (für Wohnungsgeberbestätigung u. a.) */
export interface EinheitDaten {
  bezeichnung?: string | null; etage?: string | null; personen?: string[];
  wohnflaeche?: number | string | null; zimmer?: number | string | null; badezimmer?: number | string | null; typ?: string | null;
}
/** #590: Eigentum im Objekt (WEG: MEA, Hausgeld; Einheiten des Eigentümers) */
export interface EigentumDaten { einheiten: string[]; mea: number; hausgeld: number; ruecklage: number; ab?: string | null; bis?: string | null }

const VERWALTUNGSART: Record<string, string> = { MV: 'Mietverwaltung', WEG: 'WEG-Verwaltung', SEV: 'Sondereigentumsverwaltung', GV: 'Gewerbeverwaltung' };
const zahlText = (v: number | string | null | undefined, einheit = '') => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? '' : `${n.toLocaleString('de-DE', { maximumFractionDigits: 2 })}${einheit}`;
};

/** „Musterweg 1, 35037 Marburg“ – leer, wenn nichts da ist */
export function anschriftZeile(a: { strasse?: string | null; hausnummer?: string | null; plz?: string | null; ort?: string | null }): string {
  return [[t(a.strasse), t(a.hausnummer)].filter(Boolean).join(' '), [t(a.plz), t(a.ort)].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/** Name eines Eigentümers: Firma, sonst Vor- und Nachname */
const eigName = (e: EigentuemerDaten | null | undefined) => t(e?.firma_name) || [t(e?.vorname), t(e?.nachname)].filter(Boolean).join(' ');

/** Platzhalter-Werte (Gruppen wie in briefVorlagen.ts). Fehlendes bleibt leer. */
export function briefWerte(args: { mieter?: MieterDaten | null; objekt?: ObjektDaten | null; eigentuemer?: EigentuemerDaten | null; einheit?: EinheitDaten | null; eigentum?: EigentumDaten | null; heute: Date }): Record<string, string> {
  const { mieter: m, objekt: o, eigentuemer: e, einheit: w, eigentum: ew, heute } = args;
  // #592: Zustellanschrift des Eigentümers hat Vorrang
  const zustell = !!(t(e?.zustell_strasse) || t(e?.zustell_plz));
  const eigAnschrift = e ? anschriftZeile(zustell
    ? { strasse: e.zustell_strasse, hausnummer: e.zustell_hausnummer, plz: e.zustell_plz, ort: e.zustell_stadt }
    : { strasse: e.strasse, hausnummer: e.hausnummer, plz: e.plz, ort: e.stadt }) : '';
  const mieterName = m ? (t(m.firma_name) || [t(m.vorname), t(m.nachname)].filter(Boolean).join(' ')) : '';
  const summe = m ? [m.kaltmiete, m.nebenkosten_vorauszahlung, m.heizkosten, m.weitere_kosten].reduce<number>((s, x) => s + (Number(x) || 0), 0) : 0;
  return {
    'mieter.anrede': t(m?.anrede), 'mieter.vorname': t(m?.vorname), 'mieter.nachname': t(m?.nachname) || t(m?.firma_name),
    'mieter.email': t(m?.email), 'mieter.telefon': t(m?.telefon) || t(m?.mobil), 'mieter.geburtsdatum': datumDe(m?.geburtsdatum),
    'objekt.bezeichnung': t(o?.bezeichnung), 'objekt.strasse': t(o?.strasse), 'objekt.hausnummer': t(o?.hausnummer),
    'objekt.plz': t(o?.plz), 'objekt.ort': t(o?.stadt),
    'vertrag.vertragsnummer': t(m?.kundennummer), 'vertrag.beginn': datumDe(m?.mietbeginn),
    'vertrag.ende': m ? (datumDe(m.mietende) || 'unbefristet') : '',
    'vertrag.kaltmiete': euro(m?.kaltmiete), 'vertrag.nebenkosten': euro(m?.nebenkosten_vorauszahlung),
    'vertrag.gesamtmiete': m && summe > 0 ? euro(summe) : '', 'vertrag.kaution': euro(m?.kaution),
    'eigentuemer.vorname': t(e?.vorname), 'eigentuemer.nachname': t(e?.nachname), 'eigentuemer.firma_name': t(e?.firma_name),
    'eigentuemer.name': eigName(e), 'eigentuemer.anschrift': eigAnschrift,
    'eigentuemer.name_anschrift': [eigName(e), eigAnschrift].filter(Boolean).join(', '),
    'objekt.anschrift': o ? anschriftZeile({ strasse: o.strasse, hausnummer: o.hausnummer, plz: o.plz, ort: o.stadt }) : '',
    'mieter.name': mieterName,
    'mieter.personen': (w?.personen?.length ? w.personen : mieterName ? [mieterName] : []).join(', '),
    'einheit.bezeichnung': t(w?.bezeichnung),
    'einheit.lage': t(w?.etage) || t(w?.bezeichnung),
    // #590: weitere Stammdaten nach der Immoware-Liste (B-, E-, O-, G-, VE-Platzhalter)
    'mieter.iban': t(m?.mieter_iban) || t(m?.bankverbindung), 'mieter.kontoinhaber': t(m?.mieter_kontoinhaber),
    'mieter.sepa_mandatsreferenz': t(m?.sepa_mandatsreferenz), 'mieter.sepa_mandatsdatum': datumDe(m?.sepa_mandatsdatum),
    'mieter.anzahl_bewohner': zahlText(m?.anzahl_bewohner),
    'vertrag.heizkosten': euro(m?.heizkosten), 'vertrag.weitere_kosten': euro(m?.weitere_kosten), 'vertrag.zahlungsweise': t(m?.zahlungsweise),
    'eigentuemer.anrede': t(e?.anrede), 'eigentuemer.briefanrede': e ? briefAnrede({ anrede: e.anrede, vorname: e.vorname, nachname: e.nachname }) : '',
    'eigentuemer.email': t(e?.email), 'eigentuemer.telefon': t(e?.telefon) || t(e?.mobil),
    'eigentuemer.iban': t(e?.bankverbindung), 'eigentuemer.bic': t(e?.bic), 'eigentuemer.kontoinhaber': t(e?.kontoinhaber),
    'eigentuemer.glaeubiger_id': t(e?.glaeubiger_id), 'eigentuemer.sepa_mandatsreferenz': t(e?.sepa_mandatsreferenz),
    'eigentuemer.kundennummer': t(e?.kundennummer),
    'eigentuemer.vertrag_beginn': datumDe(e?.vertragsstart), 'eigentuemer.vertrag_ende': datumDe(e?.vertragsende),
    'eigentuemer.einheiten': ew ? ew.einheiten.join(', ') : '',
    'eigentuemer.mea': ew && ew.mea > 0 ? zahlText(ew.mea) : '',
    'eigentuemer.hausgeld': ew && ew.hausgeld + ew.ruecklage > 0 ? euro(ew.hausgeld + ew.ruecklage) : '',
    'eigentuemer.ruecklage': ew && ew.ruecklage > 0 ? euro(ew.ruecklage) : '',
    'eigentuemer.eigentum_seit': datumDe(ew?.ab),
    'objekt.nummer': t(o?.objektnummer), 'objekt.objekttyp': t(o?.objekttyp), 'objekt.baujahr': zahlText(o?.baujahr).replace(/\./g, ''),
    'objekt.heizungsart': t(o?.heizungsart), 'objekt.wohnflaeche': zahlText(o?.wohnflaeche, ' m²'), 'objekt.energieklasse': t(o?.energieklasse),
    'objekt.verwaltungsart': VERWALTUNGSART[t(o?.verwaltungsart).toUpperCase()] ?? t(o?.verwaltungsart),
    'einheit.wohnflaeche': zahlText(w?.wohnflaeche, ' m²'), 'einheit.zimmer': zahlText(w?.zimmer), 'einheit.badezimmer': zahlText(w?.badezimmer),
    'einheit.typ': t(w?.typ),
    'datum.heute': heute.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    'datum.heute_lang': heute.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }),
  };
}
